/**
 * FilterToggle component - the "Customize PDF pack" button used to expand/collapse filters.
 *
 * Desktop: compact row with panel icon.
 * Mobile: full-width accordion header with chevron (expand_more / expand_less).
 */

import React from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';

export interface FilterToggleProps {
  /** Whether the filter panel is currently expanded */
  expanded: boolean;
  /** Callback when toggled */
  onToggle: (expanded: boolean) => void;
  /**
   * When true, forces mobile accordion layout (full-width header, chevron
   * icon) regardless of CSS breakpoint.
   */
  mobileLayout?: boolean;
  /** Optional className */
  className?: string;
}

export function FilterToggle({
  expanded,
  onToggle,
  mobileLayout = false,
  className,
}: FilterToggleProps) {
  const cls = `${className ?? ''} ${mobileLayout ? 'mobileLayout' : ''}`.trim();
  return (
    <CustomizeHeader
      type="button"
      onClick={() => onToggle(!expanded)}
      className={cls || undefined}
    >
      <Body size="md" as="span">
        Customize PDF pack
      </Body>
      <DesktopIcon
        className={mobileLayout ? 'mobileLayout' : undefined}
        aria-hidden
      >
        <Icon name="left_panel_close" size={18} />
      </DesktopIcon>
      <MobileIcon
        className={mobileLayout ? 'mobileLayout' : undefined}
        aria-hidden
      >
        <Icon name={expanded ? 'expand_less' : 'expand_more'} size={20} />
      </MobileIcon>
    </CustomizeHeader>
  );
}

const CustomizeHeader = styled.button`
  @import 'stylesheets/design_system/breakpoint';

  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  cursor: pointer;
  color: var(--text-primary, #141413);
  white-space: nowrap;

  @include breakpoint(small) {
    width: 100%;
    justify-content: space-between;
    padding: var(--spacing-xs-16, 16px);
  }

  &.mobileLayout {
    width: 100%;
    justify-content: space-between;
    padding: var(--spacing-xs-16, 16px);
  }
`;

const DesktopIcon = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: var(--corner-radius-radius-sm, 4px);
  background: var(--background-light-blue, #eef2f9);
  color: var(--text-link, #0645b1);

  @include breakpoint(small) {
    display: none;
  }

  &.mobileLayout {
    display: none;
  }
`;

const MobileIcon = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small) {
    display: inline-flex;
    color: var(--text-primary, #141413);
  }

  &.mobileLayout {
    display: inline-flex;
    color: var(--text-primary, #141413);
  }
`;

export default FilterToggle;
